import { useEffect, useRef } from 'react';

/**
 * 입력값이 빠르게 바뀔 때 마지막 값에서만 콜백을 1회 호출.
 * - 컴포넌트 unmount 시 마지막 pending 호출은 자동 cancel.
 * - flush() 로 즉시 실행 가능.
 */
export function useDebouncedCallback<T>(
  value: T,
  delayMs: number,
  fn: (v: T) => void,
): {
  flush: () => void;
} {
  const fnRef = useRef(fn);
  const valueRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialRef = useRef(true);

  fnRef.current = fn;
  valueRef.current = value;

  // biome-ignore lint/correctness/useExhaustiveDependencies: fnRef/valueRef 는 ref 라 의존성 불필요. value 가 바뀌면 디바운스 timer 만 새로 잡음.
  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      fnRef.current(valueRef.current);
    }, delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delayMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const flush = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    fnRef.current(valueRef.current);
  };
  return { flush };
}
